import { Injectable } from '@angular/core';
import { IMyPosReceiptItem } from '@core/services/system/receipt-printers/classes/receipt-builder.class';
import { PAYMENT_PROVIDERS, PAYMENT_TRANSACTION } from '@shared/constants/payment-providers.const';
import { PaymentResult } from '@shared/classes/payment-result.class';
import { PaymentProcessingService } from '@shared/components/payment-processing/payment-processing.service';
import {
  MyPosTransactionCodeError,
  MyPosTransactionMessageError,
  MyPosTransactionResponseCode,
} from './constants/my-pos-transaction-models.enum';
import { PaymentProcessingActions } from '@shared/components/payment-processing/payment-processing-actions.enum';
import { TranslateService } from '@ngx-translate/core';
import {
  IMyPosInfo,
  MyPosN5Plugin,
  IMyPosTransactionResult,
  MyPosScannerResult,
  MyposN5PluginError,
  MyPosScannerType,
} from '@core/capacitor-plugins/capacitor-plugins.class';
import { LogService } from '@core/services/system/logger/log.service';
import { Observable } from 'rxjs';
import { DefaultPaymentMethods, LOCALE } from '@shared/constants';
import { MyPosMakeTransactionOptions } from './my-pos-types';

@Injectable()
export class MyPosFacade {
  private readonly logger = this.logService.createLogger('MyPosFacade');
  constructor(
    private paymentProcessingService: PaymentProcessingService,
    private translateService: TranslateService,
    private logService: LogService
  ) {}

  info(): Promise<IMyPosInfo> {
    return MyPosN5Plugin.info();
  }

  pay(options: MyPosMakeTransactionOptions): Promise<IMyPosTransactionResult> {
    const { amount, currency, invoiceUuid, paymentMethod } = options;
    return MyPosN5Plugin.pay({
      amount,
      currency,
      paymentMethod: paymentMethod === DefaultPaymentMethods.TWINT ? 'twint' : undefined,
      foreignTransactionId: invoiceUuid,
    });
  }

  refund(options: MyPosMakeTransactionOptions): Promise<IMyPosTransactionResult> {
    const { amount, currency, invoiceUuid, paymentMethod } = options;
    return MyPosN5Plugin.refund({
      amount: Math.abs(amount),
      currency,
      paymentMethod: paymentMethod === DefaultPaymentMethods.TWINT ? 'twint' : undefined,
      foreignTransactionId: invoiceUuid,
    });
  }

  print(printData: IMyPosReceiptItem[]): Promise<void> {
    return MyPosN5Plugin.print(printData);
  }

  startScanner(scannerType: MyPosScannerType): Observable<MyPosScannerResult> {
    return MyPosN5Plugin.startScanner(scannerType);
  }

  openCashDrawer(): Promise<void> {
    return MyPosN5Plugin.openCashDrawer();
  }

  async makeTransaction(options: MyPosMakeTransactionOptions): Promise<PaymentResult> {
    try {
      const { transactionType, amount, paymentMethod } = options;
      const paymentResult = new PaymentResult(PAYMENT_PROVIDERS.MYPOS);
      const isRefund = transactionType === PAYMENT_TRANSACTION.PAYMENT_REFUND || amount < 0;
      if (paymentMethod === DefaultPaymentMethods.TWINT && !(await this.isEnabledTwint())) {
        return paymentResult;
      }
      const transactionResult: IMyPosTransactionResult = isRefund ? await this.refund(options) : await this.pay(options);
      paymentResult.setPaymentResultData(transactionResult);
      return paymentResult;
    } catch (error) {
      error = error as MyposN5PluginError;
      this.logger.error(error, 'makeTransaction');
      let { status, responseCode } = error;
      if (responseCode === MyPosTransactionResponseCode.NOT_SUPPORTED) {
        status = MyPosTransactionCodeError.TWINT_NOT_SUPPORTED;
      }
      // Native reports a cancel without result intent as status-less 'CANCELLED'.
      if (responseCode === MyPosTransactionResponseCode.CANCELLED) {
        status = MyPosTransactionCodeError.USER_CANCEL_PAYMENT;
      }
      // Never show the raw native text (e.g. 'Transaction error'): it has no translation.
      const message =
        MyPosTransactionMessageError[status] || MyPosTransactionMessageError[MyPosTransactionCodeError.TRANSACTION_FAILED];
      this.paymentProcessingService.init();
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.retry, {
        message: this.translateService.instant(message),
        retryButtonOff: true,
      });
      throw message;
    }
  }

  private async isEnabledTwint() {
    const info = await this.info();
    return info.currencyName === LOCALE.Currency.CHF;
  }
}
